import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { Move } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getChartColors } from '../utils/chartTheme';

// Initialize exporting modules
HighchartsExporting(Highcharts);
HighchartsExportData(Highcharts);

const DonutChart = ({ id, year, data, editing, branding }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const chartRef = useRef(null);
  const { titleColor, chart1, chart2, chart3 } = getChartColors(branding);

  const chartData = [
    { name: 'Scope 1', y: data?.totalScope1, color: chart1 },
    { name: 'Scope 2', y: data?.totalScope2, color: chart2 },
    { name: 'Scope 3', y: data?.totalScope3, color: chart3 },
  ];

  const updateCenterLabel = (chart) => {
    const series = chart.series[0];
    const total = series.data.reduce((sum, point) => (point.visible ? sum + point.y : sum), 0).toFixed(2);
    let customLabel = chart.options.chart.custom.label;

    if (!customLabel) {
      customLabel = chart.options.chart.custom.label = chart.renderer
        .label(`<span style="font-size: 1.8em;"><strong>${total}</strong></span><br/><span>Total tCO2e</span>`)
        .css({
          color: '#000',
          textAnchor: 'middle',
          fontFamily: 'Open Sans',
        })
        .add();
    } else {
      customLabel.attr({
        text: `<span style="font-size: 1.8em;"><strong>${total}</strong></span><br/><span>Total tCO2e</span>`,
      });
    }

    const x = series.center[0] + chart.plotLeft;
    const y = series.center[1] + chart.plotTop - customLabel.attr('height') / 2;

    customLabel.attr({ x, y });
    customLabel.css({ fontSize: `${series.center[2] / 13}px` });
  };

  const options = {
    credits: false,
    chart: {
      type: 'pie',
      height: 500,
      borderRadius: 15,
      spacing: [20, 20, 20, 20],
      custom: {},
      events: {
        render() {
          const chart = this,
            series = chart.series[0];
          let customLabel = chart.options.chart.custom.label;

          if (!customLabel) {
            customLabel = chart.options.chart.custom.label = chart.renderer
              .label(`<span style="font-size: 1.8em;" ><strong>${series.total.toFixed(2)}</strong></span><br/>Total tCO2e`)
              .css({
                color: '#000',
                textAnchor: 'middle',
                fontFamily: 'Roboto, sans-serif',
              })
              .add();
          }

          const x = series.center[0] + chart.plotLeft,
            y = series.center[1] + chart.plotTop - customLabel.attr('height') / 2;

          customLabel.attr({
            x,
            y,
          });
          // Set font size based on chart diameter
          customLabel.css({
            fontSize: `${series.center[2] / 13}px`,
          });
        },
        redraw: function () {
          const chart = this;
          updateCenterLabel(chart);
        },
      },
    },
    title: {
      text: `${year} Carbon Emissions by Scope`,
      style: {
        fontSize: '18px',
        fontFamily: 'Open Sans',
        fontWeight: '600',
        color: titleColor,
      },
    },
    plotOptions: {
      pie: {
        innerSize: '90%',
        depth: 0,
        dataLabels: {
          enabled: false,
          format: '{point.percentage:.1f} %',
          style: {
            fontSize: '12px', // Adjust font size here
          },
        },
        borderRadius: '15px',
        showInLegend: true,
      },
    },
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      x: -5,
      y: 10,
      labelFormatter: function () {
        return (
          this.name +
          `<br/><span style="margin-top: 4;"><strong>${this.percentage?.toFixed(2)}% (${this.y?.toFixed(
            2,
          )} tCO2e)</strong></span>`
        );
      },
      useHTML: true,
      custom: {
        scrollableLegendArea: {
          //minHeight: 250,
        },
      },
      navigation: {
        enabled: false, // Disable legend pagination
      },
      squareSymbol: false,
      symbolPadding: 5,
      symbolWidth: 18,
      symbolHeight: 4,
      symbolRadius: 0,
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        return `
        <strong>${this.point.name}</strong><br>
        <span style="font-size: 12px;"><span style="color:${this.color};">\u25CF</span> ${this.series.name}: 
        <strong>${this.point.y.toFixed(2)}</strong> tCO2e
      `;
      },
    },
    series: [
      {
        name: 'Emissions',
        data: chartData,
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            chart: {
              height: 450,
            },
          },
        },
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
    exporting: {
      enabled: true,
    },
  };

  const style = { transition, transform: CSS.Transform.toString(transform) };

  if (!data || data?.totalEmissions === 0) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      {editing && (
        <div className='chart-move' style={{ style }} ref={setNodeRef} {...attributes} {...listeners}>
          <Move size={16} strokeWidth={1.5} className='move-icon' title='Move Chart' />
        </div>
      )}
      <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
    </div>
  );
};

const mapStateToProps = (state) => ({ branding: state.branding });

export default connect(mapStateToProps)(DonutChart);
